import { CategoryScale, Chart, ChartOptions, LinearScale, LineElement, registerables, TimeScale } from 'chart.js';
import 'chartjs-adapter-moment';
import zoomPlugin from 'chartjs-plugin-zoom';
import * as React from 'react';
import { Line } from 'react-chartjs-2';
import { useTheme } from 'styled-components';
import { ResetZoomButton } from '../../../../../../../components/ResetZoomButton';
import { Section } from '../../../../../../../design-system';
import { ChargePointLogs } from '../../../../../../../utils/ChargePointLogs';
import { updateChartZoomLocations } from '../../../../../../../utils/chart';
import { ChargePointLogEventTypes, EventChartDatum, EventChartModel } from '../utils/EventChartModel';
import { CHART_DATE_FORMAT, diagnosticsChartIds, DiagnosticsChartType } from '../utils/meteringCharts';

Chart.register(LineElement, CategoryScale, LinearScale, TimeScale, zoomPlugin, ...registerables);

const chartOptions = (startingDate: string, endDate: string): ChartOptions<'line'> => ({
  maintainAspectRatio: false,
  parsing: false,
  showLine: false,
  scales: {
    x: { type: 'time', time: { tooltipFormat: CHART_DATE_FORMAT }, min: startingDate, max: endDate },
    y: {
      min: 0,
      max: ChargePointLogEventTypes.length,
      afterFit(scale) {
        // eslint-disable-next-line no-param-reassign -- -- Reassigning the `scale` param's properties is the accepted way to set dimensions in ChartJS
        scale.width = 100;
      },
      ticks: {
        callback: (tickValue) => (typeof tickValue === 'number' ? ChargePointLogEventTypes[tickValue] : tickValue),
        stepSize: 1,
        sampleSize: ChargePointLogEventTypes.length,
        autoSkip: false,
        crossAlign: 'near',
      },
      afterSetDimensions: (scale) => {
        // eslint-disable-next-line no-param-reassign -- Reassigning the `scale` param's properties is the accepted way to set dimensions in ChartJS
        scale.maxWidth = 100;
      },
    },
  },
  interaction: { intersect: false, mode: 'nearest', axis: 'xy' },
  animation: false,
  plugins: {
    legend: { position: 'bottom' },
    tooltip: {
      callbacks: {
        label(context) {
          const datum = context.raw as EventChartDatum;
          return EventChartModel.getTooltip(datum);
        },
      },
      mode: 'nearest',
    },
    zoom: {
      pan: { enabled: true, modifierKey: 'ctrl', mode: 'x' },
      zoom: {
        onZoomComplete: (selectedChart) =>
          updateChartZoomLocations<DiagnosticsChartType>(selectedChart, diagnosticsChartIds),
        mode: 'x',
        drag: { enabled: true },
      },
    },
  },
});

export const EventChart: React.FC<{
  data: ChargePointLogs;
  startingDate: string;
  endDate: string;
}> = ({ data, startingDate, endDate }) => {
  const { palette } = useTheme();
  const model = new EventChartModel({ logs: data, palette });
  return (
    <>
      <ResetZoomButton<DiagnosticsChartType>
        chartIds={diagnosticsChartIds}
        minXValue={startingDate}
        maxXValue={endDate}
      />
      <Section style={{ height: '60vh', position: 'relative' }}>
        <Line
          id={diagnosticsChartIds.event}
          options={chartOptions(startingDate, endDate)}
          data={{ datasets: model.toChartData() }}
        />
      </Section>
    </>
  );
};
